import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import { addWeeks, isAfter, isBefore, min } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { partition, uniq, uniqueId } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { isAssignmentHidden } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { getFriendlyDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { isRenewalCareConsent } from '@lyrahealth-inc/shared-app-logic/src/features/consent/consentUtils'
import { isCTPOForProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode, Goal } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { Provider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { Divider } from '../../atoms/divider/Divider'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { AppContext } from '../../context'
import { CollapsibleSection } from '../../molecules/collapsibleSection/CollapsibleSection'
import { GoalsCard } from '../../molecules/goalsCard/GoalsCard'
import { AssignmentsList, AssignmentsListProps, ItemAction } from '../../organisms/assignmentsList/AssignmentsList'
import { AssignmentsListItem } from '../../organisms/assignmentsList/AssignmentsListItem'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type SSCAssignmentsListProps = {
  activities: Assignment[]
  activityActions?: Array<ItemAction>
  addTrackHandler?: () => void
  appointments?: Appointment[]
  assessments: Assignment[]
  currentEpisode: Episode
  goals?: {
    [key: string]: Goal[]
  }
  itemClickHandler?: (val: any) => void
  providers?: Provider[]
  sessionCount?: number
  userRole: string
  combineLinkedAssignments?: boolean
  simplifyStatus?: boolean
  assignmentsInfo?: AssignmentsListProps['assignmentsInfo']
}

const CollapsibleSectionContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['24px'],
}))

const CurrentAssessmentsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const GoalsCardDivider = styled(Divider)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['40px'],
  marginBottom: spacing['40px'],
}))

const ActivityGroupContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  paddingBottom: spacing['8px'],
}))

const GoalsCardContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['12px'],
}))

const HeadingContainer = styled.View({
  justifyContent: 'center',
})

export const SSCAssignmentsList: React.FC<SSCAssignmentsListProps> = ({
  activities,
  activityActions = [],
  addTrackHandler,
  assessments,
  combineLinkedAssignments = false,
  currentEpisode,
  appointments,
  goals = {},
  itemClickHandler,
  providers = [],
  sessionCount,
  simplifyStatus = false,
  userRole,
  assignmentsInfo,
}) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { colors } = useTheme()
  const shouldHideGoalsSection = isCTPOForProgram(currentEpisode.program_taxonomy, ProgramNames.PerformanceCoaching)

  if (!appointments || appointments.length === 0) {
    const selectedProvider = providers?.find(
      (provider: Provider) => provider.provider.lyra_id === currentEpisode.provider_id,
    )
    appointments = selectedProvider?.appointments ?? []
  }
  const [currentEpisodeAssessments, otherEpisodeAssessments] = partition(assessments, ['episode_id', currentEpisode.id])
  const assignments = [...activities, ...currentEpisodeAssessments]
  const { trackEvent } = useContext(AppContext)

  const distinctWeeks: number[] =
    currentEpisode.curriculum[0].contents.length === 0
      ? [0]
      : uniq(assignments.map(({ session_period }) => Number(session_period) - 1)).sort((a: number, b: number) => a - b)

  const firstAppointmentDateTime = min(
    appointments
      .filter((appointment: Appointment) => appointment.episodeId === currentEpisode.id)
      .map(
        (appointment) =>
          new Date(zonedTimeToUtc(`${appointment.startDate}T${appointment.startTime}`, appointment.timeZone)),
      ),
  )

  const getAssignmentsToDisplay = (week: number) => {
    const weekAssignments = assignments.filter(
      (assignment: Assignment) => assignment.session_period === Number(week) + 1,
    )

    const contentsNames: string[] =
      week > 0 ? currentEpisode?.curriculum[week - 1].contents.map((content) => content.name) : []

    const weekRenewalConsents = weekAssignments.filter((assignment: Assignment) =>
      isRenewalCareConsent({ assignment, assignments }),
    )
    const isNotRenewalConsent = (assignment: Assignment) => !weekRenewalConsents.includes(assignment)

    const weekCurriculumAssignments = weekAssignments
      .filter(
        (assignment: Assignment) => isNotRenewalConsent(assignment) && contentsNames.includes(assignment.content.name),
      )
      .sort(
        (assignmentA: Assignment, assignmentB: Assignment) =>
          contentsNames.indexOf(assignmentA.content.name) - contentsNames.indexOf(assignmentB.content.name),
      )
    const weekNonCurriculumAssessments = weekAssignments.filter(
      (assignment: Assignment) =>
        isNotRenewalConsent(assignment) &&
        assignment.content.group === 'assessment' &&
        !contentsNames.includes(assignment.content.name),
    )
    const weekNonCurriculumOtherAssignments = weekAssignments.filter(
      (assignment: Assignment) =>
        assignment.content.group !== 'assessment' && !contentsNames.includes(assignment.content.name),
    )

    // Linking, hiding and renewal checks compare against the whole episode (`assignments`), not just
    // this week — a renewal consent can sit in a different week from the original it replaces.
    const filteredAssignments: Assignment[] = combineLinkedAssignments
      ? [...weekRenewalConsents, ...weekCurriculumAssignments, ...weekNonCurriculumAssessments].filter(
          (assignment) =>
            !isAssignmentHidden({
              currentAssignment: assignment,
              assignments,
            }),
        )
      : [...weekRenewalConsents, ...weekCurriculumAssignments, ...weekNonCurriculumAssessments]

    const weekNumberedAssignments: Assignment[] = filteredAssignments.map((assignment: Assignment, index: number) => ({
      ...assignment,
      content: {
        ...assignment.content,
        title: formatMessage(
          {
            defaultMessage: '{number}. {title}',
            description: 'Activity title numbered by its position in the week, e.g. "2. Awareness Log"',
          },
          { number: index + 1, title: assignment.content.title },
        ),
      },
    }))

    const weekDisplayedAssignments = weekNumberedAssignments.concat(
      combineLinkedAssignments
        ? weekNonCurriculumOtherAssignments.filter(
            (assignment) =>
              !isAssignmentHidden({
                currentAssignment: assignment,
                assignments,
              }),
          )
        : weekNonCurriculumOtherAssignments,
    )

    return { weekDisplayedAssignments }
  }

  const getDueDate = (week: number) => addWeeks(firstAppointmentDateTime, week)

  const isCurrentWeek = (week: number) => {
    const currentDateTime = new Date()
    return (
      (isAfter(currentDateTime, getDueDate(week - 1)) && isBefore(currentDateTime, getDueDate(week))) ||
      (week === 6 && isAfter(currentDateTime, getDueDate(6)))
    )
  }

  const getWeekItem = (week: number) => {
    const { weekDisplayedAssignments }: { weekDisplayedAssignments: Assignment[] } = getAssignmentsToDisplay(week)

    const completedAssignments = weekDisplayedAssignments.reduce(
      (completed, assignment) =>
        completed +
        (assignment.status === 'completed' ||
        (assignment?.assignment_responses &&
          assignment?.assignment_responses.filter((response) => response.status === 'completed').length > 0)
          ? 1
          : 0),
      0,
    )

    const title =
      week < 1
        ? formatMessage({
            defaultMessage: 'To complete before session',
            description: 'Title on pre-session assignments list for Guided Self Care',
          })
        : formatMessage(
            {
              defaultMessage: 'Week {number} - {title}',
              description: "Title on week's assignments list for Guided Self Care",
            },
            {
              number: week,
              title: currentEpisode?.curriculum[week - 1]?.title,
            },
          )

    return (
      <CollapsibleSectionContainer key={week}>
        <CollapsibleSection
          title={title}
          subtitle={formatMessage(
            {
              defaultMessage: 'Complete by {date}',
              description:
                'Subtitle of a week of activities with when they are due, e.g. "Complete by May 14th". {date} can also be "today", "tomorrow", "yesterday" or a weekday',
            },
            {
              date: getFriendlyDate({
                date: getDueDate(week),
                intl,
                displayTime: false,
                trackEvent,
              }),
            },
          )}
          initiallyOpen={distinctWeeks.length === 1 || isCurrentWeek(week)}
          rightItems={formatMessage(
            {
              defaultMessage: '{completed}/{total}',
              description: 'Number of completed activities out of the total for the week, e.g. "1/3"',
            },
            { completed: completedAssignments, total: weekDisplayedAssignments.length },
          )}
          content={
            <>
              {weekDisplayedAssignments.map((assignment: Assignment, idx: number) => (
                <AssignmentsListItem
                  key={uniqueId('assignmentListItem')}
                  assignment={assignment}
                  assignments={assignments}
                  itemActions={activityActions}
                  itemClickHandler={itemClickHandler}
                  sessionCount={sessionCount}
                  showDueDate={false}
                  showProviderName={false}
                  userRole={userRole}
                  isFirstItem={false}
                  isLastItem={idx === weekDisplayedAssignments.length - 1}
                  combineLinkedAssignments={combineLinkedAssignments}
                  simplifyStatus={simplifyStatus}
                  assignmentsInfo={assignmentsInfo}
                />
              ))}
            </>
          }
        />
      </CollapsibleSectionContainer>
    )
  }

  return (
    <View testID={tID('SSCAssignmentsList-currentAssignments')}>
      <View testID={tID('SSCAssignmentsList-weekAssignmentsContainer')}>
        {distinctWeeks.map((week: number) => getWeekItem(week))}
      </View>
      {userRole === 'client' && otherEpisodeAssessments.length > 0 && (
        <CurrentAssessmentsContainer testID={tID('SSCAssignmentsList-currentAssessmentsContainer')}>
          <Title
            selectable={false}
            text={formatMessage({
              defaultMessage: 'Current assessments',
              description: 'Header for list of assessments from other episodes that GSC client needs to complete',
            })}
            size={TitleSize.MEDIUM}
            testID={tID('SSCAssignmentsList-currentAssessmentstitle')}
            wrap
            noRole
          />
          <AssignmentsList
            assignments={otherEpisodeAssessments}
            userRole={userRole}
            itemActions={activityActions}
            itemClickHandler={itemClickHandler}
            combineLinkedAssignments={combineLinkedAssignments}
            simplifyStatus={simplifyStatus}
            providers={providers}
          />
        </CurrentAssessmentsContainer>
      )}

      {userRole === 'client' && goals[currentEpisode.id]?.length > 0 && !shouldHideGoalsSection && (
        <>
          <GoalsCardDivider height={1} color={colors.borderDefault} />
          <ActivityGroupContainer>
            <HeadingContainer>
              <Title
                size={TitleSize.XSMALL}
                text={formatMessage({
                  defaultMessage: 'Reflect on your care',
                  description: 'Header text explaining a user can look back on their goals',
                })}
                level='3'
              />
            </HeadingContainer>
          </ActivityGroupContainer>

          <GoalsCardContainer>
            <GoalsCard
              goal1={goals[currentEpisode.id][0]?.goal_text ?? ''}
              goal2={goals[currentEpisode.id][1]?.goal_text}
              goal3={goals[currentEpisode.id][2]?.goal_text}
            />
          </GoalsCardContainer>
        </>
      )}

      {userRole === 'provider' && distinctWeeks.length === 1 && addTrackHandler && (
        <PrimaryButton
          testID={tID('SSCAssignmentsList-addTrackBtn')}
          onPress={addTrackHandler}
          text={formatMessage({
            defaultMessage: 'Add Care Plan',
            description: 'Button to add a track for a GSC client',
          })}
        />
      )}
    </View>
  )
}
